import { debounce, omit, pick, sortBy as _sortBy } from "lodash-es";
import {
  FilterType,
  FormattedFilter,
  RangesData,
  IUiFilterTag,
} from "ecom-types";

import { filterQueryKey } from "~/configs";
export const useCatalogFilters = ({
  minMaxData,
  collectionIds,
  categoryIds,
  hideFilterType,
  showCategoriesFilters,
  showCollectionsFilters,
  showAll,
}: {
  isEngagement?: boolean;
  minMaxData?: RangesData;
  collectionIds?: number[];
  categoryIds?: number[];
  hideFilterType?: string[];
  showCategoriesFilters?: boolean;
  showCollectionsFilters?: boolean;
  showAll?: boolean;
} = {}) => {
  const route = useRoute();
  const router = useRouter();
  const { t, n } = useI18n();

  const sortOptions = [
    {
      label: t("default"),
      value: "defaultAsc",
      column: "collection_sort_order",
      order_by: "asc",
    },
    {
      label: t("bestSellers"),
      value: "popularityDesc",
      column: "bestseller",
      order_by: "desc",
    },
    {
      label: `${t("productName")} (A-Z)`,
      value: "nameAsc",
      column: "name",
      order_by: "asc",
    },
    {
      label: `${t("productName")} (Z-A)`,
      value: "nameDesc",
      column: "name",
      order_by: "desc",
    },
    {
      label: `${t("price")} (${t("highToLow")})`,
      value: "priceDesc",
      column: "price",
      order_by: "desc",
    },
    {
      label: `${t("price")} (${t("lowToHigh")})`,
      value: "priceAsc",
      column: "price",
      order_by: "asc",
    },
    // {
    //   label: "Alphabetical",
    //   value: "nameAsc",
    //   column: "name",
    //   order_by: "asc",
    // },
  ];

  // if (isEngagement) {
  //   sortOptions.push({
  //     label: "Sort by shape",
  //     value: "defaultconfig_materials_DIAMOND_SHAPE_asc",
  //     column: "defaultconfig_materials_DIAMOND_SHAPE",
  //     order_by: "asc",
  //   });
  //   sortOptions.push({
  //     label: "By Setting Carat Weight",
  //     value: "materials_SETTING_CT_WT_asc",
  //     column: "materials_SETTING_CT_WT",
  //     order_by: "asc",
  //   });
  // } else {
  //   sortOptions.push({
  //     label: "By Total Carat Weight",
  //     value: "materials_CARAT_WEIGHTS_asc",
  //     column: "materials_CARAT_WEIGHTS",
  //     order_by: "asc",
  //   });
  // }

  // const sortByValue = ref<any>(null);
  // const sortBy = computed({
  //   get() {
  //     return (
  //       sortOptions.find((item) => item.value === sortByValue.value) ||
  //       sortOptions[0]
  //     );
  //   },
  //   set(value) {
  //     sortByValue.value = value?.value;
  //     if (page.value > 1) {
  //       page.value = 1;
  //     }
  //   },
  // });

  const sortBy = computed({
    get() {
      return (
        sortOptions.find((item) => item.value === route.query.sortBy) ||
        sortOptions[0]
      );
    },
    set(value) {
      router.push({
        query: { ...route.query, page: 1, sortBy: value?.value },
      });
    },
  });

  const updatePriceDebounce = debounce((value) => {
    router.replace({
      query: {
        ...route.query,
        cost_min: value[0],
        cost_max: value[1],
      },
    });
  }, 300);

  const price = computed<[number | string, number | string] | []>({
    get() {
      return minMaxData
        ? ([
            route.query.cost_min || minMaxData.cost_min,
            route.query.cost_max || minMaxData.cost_max,
          ] as [number | string, number | string])
        : [];
    },
    set: updatePriceDebounce,
  });

  const prevPage = ref(0);

  const page = computed<number>({
    get() {
      return Number(route.query.page) || 1;
    },
    set(value) {
      router.push({
        query: { ...route.query, page: value },
      });
    },
  });

  const filterQuery = computed<{
    [key: string]: any;
  }>(() => {
    return {
      [filterQueryKey.style]: route.query[filterQueryKey.style]
        ? Array.isArray(route.query[filterQueryKey.style])
          ? route.query[filterQueryKey.style]
          : [route.query[filterQueryKey.style]]
        : [],
      [filterQueryKey.style_collections]: route.query[
        filterQueryKey.style_collections
      ]
        ? Array.isArray(route.query[filterQueryKey.style_collections])
          ? route.query[filterQueryKey.style_collections]
          : [route.query[filterQueryKey.style_collections]]
        : [],
      [filterQueryKey.style_categories]: route.query[
        filterQueryKey.style_categories
      ]
        ? Array.isArray(route.query[filterQueryKey.style_categories])
          ? route.query[filterQueryKey.style_categories]
          : [route.query[filterQueryKey.style_categories]]
        : [],
      [filterQueryKey.materials]: route.query[filterQueryKey.materials]
        ? Array.isArray(route.query[filterQueryKey.materials])
          ? route.query[filterQueryKey.materials]
          : [route.query[filterQueryKey.materials]]
        : [],
      [filterQueryKey.collections]: route.query[filterQueryKey.collections]
        ? Array.isArray(route.query[filterQueryKey.collections])
          ? route.query[filterQueryKey.collections]
          : [route.query[filterQueryKey.collections]]
        : [],
      [filterQueryKey.categories]: route.query[filterQueryKey.categories]
        ? Array.isArray(route.query[filterQueryKey.categories])
          ? route.query[filterQueryKey.categories]
          : [route.query[filterQueryKey.categories]]
        : [],
    };
  });

  const filterParams = computed(() => {
    const priceQuery = minMaxData
      ? Number(price.value[0]) === minMaxData.cost_min &&
        Number(price.value[1]) === minMaxData.cost_max
        ? {}
        : { min_cost_from: price.value[0], max_cost_to: price.value[1] }
      : {};
    return {
      ...priceQuery,
      materials_sku:
        [
          ...filterQuery.value?.[filterQueryKey.materials],
          ...filterQuery.value?.[filterQueryKey.style],
        ].filter(Boolean) || [],
      column: sortBy.value.column,
      order_by: sortBy.value.order_by,
      count: 8,
      page: page.value,
      collections:
        [
          ...filterQuery.value?.[filterQueryKey.collections],
          ...filterQuery.value?.[filterQueryKey.style_collections],
        ].filter(Boolean) || [],
      categories_sku:
        [
          ...filterQuery.value?.[filterQueryKey.categories],
          ...filterQuery.value?.[filterQueryKey.style_categories],
        ].filter(Boolean) || [],
      search: route.query.search,
    };
  });

  const { allFilters, bootstrap, filtersData } = useGeneralStore();

  const filtersGroup = computed<FormattedFilter[]>(() => {
    const filters = allFilters(collectionIds, categoryIds);
    return filters
      ? (bootstrap?.material_categories
          .map((category): FormattedFilter => {
            const data = filters.materials.reduce((acc, el) => {
              if (el.material_category_id === category.id) {
                acc.push({
                  ...el,
                  title: el?.currentTranslation?.title,
                  value: el.sku,
                });
              }
              return acc;
            }, [] as FilterType[]);
            return {
              ...category,
              key: "materials",
              value: data,
            };
          })
          .filter(
            (material) =>
              material.value.length &&
              (hideFilterType?.length
                ? !hideFilterType?.includes(material.sku)
                : true),
          ) as FormattedFilter[])
      : ([] as FormattedFilter[]);
  });

  const filtersCategories = computed(() => {
    return {
      advanced_filter: false,
      currentTranslation: {
        title: t("category"),
      },
      key: "categories",
      show_tooltip: false,
      value: filtersData?.categories?.reduce((acc, el) => {
        if (!categoryIds?.length || categoryIds?.includes(el.id)) {
          acc.push({
            ...el,
            value: el.sku,
            title: el.currentTranslation?.title,
          } as FilterType);
        }

        return acc;
      }, [] as FilterType[]),
      view_type: "round",
    };
  });

  const filtersCollections = computed(() => {
    return {
      advanced_filter: false,
      currentTranslation: {
        title: t("collection"),
      },
      key: "collections",
      show_tooltip: false,
      value: filtersData?.collections?.reduce((acc, el) => {
        if (!collectionIds?.length || collectionIds?.includes(el.id)) {
          acc.push({
            ...el,
            value: String(el.id),
            title: el.currentTranslation?.title,
          } as FilterType);
        }

        return acc;
      }, [] as FilterType[]),
      view_type: "round",
    };
  });

  const filtersGroupAll = computed<FormattedFilter[]>(() => {
    const temp: FormattedFilter[] = [];

    if (showCategoriesFilters) {
      temp.push(filtersCategories.value as FormattedFilter);
    }
    if (showCollectionsFilters) {
      temp.push(filtersCollections.value as FormattedFilter);
    }
    if (filtersGroup.value.length > 1 && showAll) {
      const allValues = filtersGroup.value.reduce((acc, el) => {
        acc.push(...el.value);
        return acc;
      }, [] as FilterType[]);
      temp.push({
        title: t("all"),
        currentTranslation: { title: t("all") },
        key: "materials",
        value: allValues,
      } as FormattedFilter);
    }

    temp.push(...filtersGroup.value);

    return temp;
  });

  const tags = computed<IUiFilterTag[]>(() => {
    const priceFilter = pick(route.query as Record<string, any>, [
      "cost_min",
      "cost_max",
    ]);

    const temp: Array<IUiFilterTag> = [];

    const queryTags = Object.entries({ ...filterQuery.value })
      .reduce(
        (acc, [key, value]) => {
          const tagList = value
            .map((val: string | undefined) => {
              return filtersGroup.value
                ?.map((item) => {
                  const findedValue = item.value.find(
                    (i) => item.key === key && i.value === val,
                  );

                  return findedValue
                    ? {
                        id: findedValue.id,
                        type: findedValue.type,
                        title: findedValue.title,
                        value: findedValue.value,
                        category_title: item.currentTranslation.title,
                        category_id: item.id,
                        category_sort_order: item.sort_order,
                      }
                    : null;
                })
                .filter((i) => i);
            })
            .flat();

          if (tagList) {
            acc.push(tagList);
          }

          return acc.filter((i) => i);
        },
        [] as Array<IUiFilterTag[]>,
      )
      .flat();

    temp.push(...queryTags);

    if (!isEmpty(priceFilter)) {
      temp.unshift({
        id: "price",
        type: "price",
        title: `${n(Number([priceFilter.cost_min]), "currencyCents")} - ${n(Number([priceFilter.cost_max]), "currencyCents")}`,
        value: `${n(Number([priceFilter.cost_min]), "currencyCents")} - ${n(Number([priceFilter.cost_max]), "currencyCents")}`,
        category_title: t("price"),
        category_id: "price",
        category_sort_order: -1,
      });
    }

    if (route.query.search) {
      temp.unshift({
        id: "search",
        type: "search",
        title: `${t("search")}: ${route.query.search}`,
        value: route.query.search as string,
        category_title: t("search"),
        category_id: "search",
        category_sort_order: -2,
      });
    }

    return _sortBy(temp.flat(), ["category_sort_order", "sort_order"]);
  });

  const tagsByCategory = computed(() => {
    return tags.value.reduce((acc, tag) => {
      if (acc[tag.category_id]) {
        acc[tag.category_id].push(tag);
      } else {
        acc[tag.category_id] = [tag];
      }
      return acc;
    }, {});
  });

  // function getOptionImage(option: FilterType) {
  //   if (!option) return undefined;
  //   return getImageByPosition(option.medias, "list")?.file?.url || undefined;
  // }
  //
  // function toggleFilter(idx: number) {
  //   openedIdx.value = openedIdx.value === idx ? -1 : idx;
  // }
  //
  function removeTag(tag: IUiFilterTag) {
    if (tag.type === "price") {
      router.push({
        query: { page: 1, ...omit(route.query, ["cost_min", "cost_max"]) },
      });
    }
    if (tag.type === "search") {
      router.push({
        query: { page: 1, ...omit(route.query, ["search"]) },
      });
    }
    if (
      filterQuery.value[tag.type] &&
      Array.isArray(filterQuery.value[tag.type])
    ) {
      updateQuery(
        filterQuery.value[tag.type].filter(
          (item: string | undefined) => item !== tag.value,
        ),
        tag.type,
      );
    }
  }

  function removeTagAll() {
    router.push({
      query: omit(route.query, [
        ...Object.keys(filterQuery.value),
        "cost_min",
        "cost_max",
        "search",
      ]),
    });
  }

  function updateQuery(value: string[], key: string) {
    router.push({ query: { ...route.query, page: 1, [key]: value } });
  }

  const filterTags = computed(() => {
    return {
      tags: tags.value,
      removeTag,
      removeTagAll,
    };
  });

  return {
    filterQuery,
    sortOptions,
    sortBy,
    filterParams,
    page,
    price,
    filtersGroup,
    filtersCategories,
    filtersCollections,
    filtersGroupAll,
    tags,
    removeTag,
    removeTagAll,
    filterTags,
    tagsByCategory,
    prevPage,
  };
};
